import { formatUserFactorModel } from '../utils/factorDataOptions';

export const APPLICATION_CONTEXT_LABELS = Object.freeze({
    factor_data: 'Factor data',
    master_security_identity: 'Security Resolution',
    watchlist_actual_portfolio: 'Watchlist and Actual Portfolio',
    position_sizing: 'Position Sizing',
    optimized_portfolio: 'Saved Optimized Portfolio',
});

export const POS_SIZING_STALE_REASON_LABELS = Object.freeze({
    prices: 'Prices changed',
    watchlist: 'Watchlist changed',
    classification: 'Watchlist attributes changed',
    state_hash: 'Position Sizing publication changed',
    factor_model: 'Factor model changed',
    factor_data_model: 'Factor data model changed',
    factor_snapshot_date: 'Factor data date changed',
    factor_data_release: 'Factor data was updated',
    factor_context: 'Saved factor context differs from current',
    factor_generation: 'Saved factor data generation differs from current',
    factor_generation_unresolved: 'Current factor data generation could not be verified',
    msi_snapshot: 'Security Resolution snapshot changed',
    beta_source: 'Beta source changed',
    idio_vol_source: 'Idio volatility source changed',
    context_metadata: 'Draft settings are missing',
    signal_scope: 'Actual portfolio positions changed',
});

const USER_VISIBLE_POS_SIZING_STALE_REASONS = new Set(
    Object.keys(POS_SIZING_STALE_REASON_LABELS),
);
// New factor data also changes the saved factor context and its generation.
// Name that one change once.
const REASONS_EXPLAINED_BY_FACTOR_DATA_RELEASE = new Set([
    'factor_context',
    'factor_generation',
]);

export function formatPosSizingStaleReasonLabel(reason) {
    return POS_SIZING_STALE_REASON_LABELS[reason]
        || String(reason || '').replaceAll('_', ' ');
}

export function getUserVisiblePosSizingStaleReasons(reasons = []) {
    const visibleReasons = (Array.isArray(reasons) ? reasons : []).filter((reason) => (
        USER_VISIBLE_POS_SIZING_STALE_REASONS.has(reason)
    ));
    if (!visibleReasons.includes('factor_data_release')) {
        return visibleReasons;
    }
    return visibleReasons.filter((reason) => (
        !REASONS_EXPLAINED_BY_FACTOR_DATA_RELEASE.has(reason)
    ));
}

export function deriveMsiContextStatus(watchlistContextStatus) {
    const aggregateStatus = watchlistContextStatus?.status;
    if (!aggregateStatus) return 'checking';
    if (aggregateStatus !== 'update_available') return aggregateStatus;
    const changedFields = watchlistContextStatus?.changed_fields;
    if (!Array.isArray(changedFields)) return 'update_available';
    return changedFields.includes('msi_snapshot_version')
        ? 'update_available'
        : 'current';
}

export const WATCHLIST_CONTEXT_RELEASE_FIELDS = Object.freeze([
    'factor_release_id',
    'factor_deployment_id',
    'provider_source_release_id',
    'factor_kind',
]);

export const WATCHLIST_CONTEXT_SOURCE_RULE_FIELDS = Object.freeze([
    'provider_source_system',
    'provider_namespace',
    'route_policy_contract',
    'route_policy_version',
    'guarded_contract_version',
]);

export const WATCHLIST_CONTEXT_SETTING_FIELDS = Object.freeze([
    'setting_version',
    'setting_digest',
]);

export const WATCHLIST_CONTEXT_PRIMARY_FACTOR_FIELDS = Object.freeze([
    'vendor',
    'factor_model',
    'factor_horizon',
    'factor_as_of_date',
    ...WATCHLIST_CONTEXT_RELEASE_FIELDS,
]);

export const WATCHLIST_CONTEXT_FIELDS = Object.freeze([
    ...WATCHLIST_CONTEXT_PRIMARY_FACTOR_FIELDS,
    'msi_snapshot_version',
    ...WATCHLIST_CONTEXT_SOURCE_RULE_FIELDS,
    ...WATCHLIST_CONTEXT_SETTING_FIELDS,
]);

// Labels for a Watchlist context refresh: `entry` names a history entry,
// `note` names a context update folded into a version.
const WATCHLIST_CONTEXT_UPDATE_LABELS = Object.freeze({
    factorModel: Object.freeze({ entry: 'Factor model change', note: 'Factor model changed' }),
    factorData: Object.freeze({ entry: 'Factor data update', note: 'Factor data updated' }),
    factorSettings: Object.freeze({ entry: 'Factor settings change', note: 'Factor settings updated' }),
    securityMaster: Object.freeze({ entry: 'Security master update', note: 'Security master updated' }),
    providerReview: Object.freeze({ entry: 'Factor match review', note: 'Factor matches reviewed' }),
    attributes: Object.freeze({ entry: 'Attribute update', note: 'Watchlist attributes updated' }),
    context: Object.freeze({ entry: 'Context update', note: 'Watchlist context updated' }),
});

// ``availableContext`` is the context the update moved to. A vendor change is
// a factor vendor switch chosen in Settings, named by its vendor.
export function watchlistContextUpdateLabels(changedFields = [], availableContext = null) {
    const changed = new Set(Array.isArray(changedFields) ? changedFields : []);
    const hasAny = (fields) => fields.some((field) => changed.has(field));
    if (changed.has('vendor') && availableContext?.vendor) {
        const vendor = formatUserFactorModel(availableContext.vendor);
        return Object.freeze({
            entry: `Switch to ${vendor} factor data`,
            note: `Switched to ${vendor} factor data`,
            phrase: `switch to ${vendor} factor data`,
        });
    }
    if (hasAny(['vendor', 'factor_model', 'factor_horizon'])) {
        return WATCHLIST_CONTEXT_UPDATE_LABELS.factorModel;
    }
    if (hasAny([...WATCHLIST_CONTEXT_RELEASE_FIELDS, 'factor_as_of_date'])) {
        return WATCHLIST_CONTEXT_UPDATE_LABELS.factorData;
    }
    if (hasAny(WATCHLIST_CONTEXT_SETTING_FIELDS)) {
        return WATCHLIST_CONTEXT_UPDATE_LABELS.factorSettings;
    }
    if (changed.has('msi_snapshot_version')) {
        return WATCHLIST_CONTEXT_UPDATE_LABELS.securityMaster;
    }
    if (changed.has('provider_identity_review')) {
        return WATCHLIST_CONTEXT_UPDATE_LABELS.providerReview;
    }
    if (changed.has('watchlist_attributes')) {
        return WATCHLIST_CONTEXT_UPDATE_LABELS.attributes;
    }
    return WATCHLIST_CONTEXT_UPDATE_LABELS.context;
}
